<template>
    <div class="content m-b-none" :class="{ 'editor-wrapper': enableWrapper, 'is-saving': isSaving }">
        <safe-froala-wrapper
            :value="model"
            :tag="'textarea'"
            :config="mergedConfig"
            @update:value="handleUpdate"
            @initialized="handleInitialized"
            @error="handleError"
        />
    </div>
</template>

<script>
import SafeFroalaWrapper from '@/components/editor/SafeFroalaWrapper.vue';
import { FILE_UPLOAD_CONTEXT, FILE_UPLOAD_REJECTION_REASON } from '@/constants/fileUploadRejection.js';
import { initialState } from '@/Helpers.js';
import axios from '@/plugins/axios.js';
import inserts from '@/plugins/froala-editor/inserts.js';
import { reportFileUploadRejection } from '@/utils/reportFileUploadRejection.js';

/**
 * Froala image error codes that mean an uploaded file was refused. Codes 1 and 7
 * come from inserting an image by link, so no file is involved.
 */
const FROALA_IMAGE_ERROR_REASONS = Object.freeze({
    2: FILE_UPLOAD_REJECTION_REASON.SERVER_ERROR,
    3: FILE_UPLOAD_REJECTION_REASON.SERVER_ERROR,
    4: FILE_UPLOAD_REJECTION_REASON.SERVER_ERROR,
    5: FILE_UPLOAD_REJECTION_REASON.FILE_TOO_LARGE,
    6: FILE_UPLOAD_REJECTION_REASON.UNSUPPORTED_FILE_TYPE,
    8: FILE_UPLOAD_REJECTION_REASON.INVALID_FILE,
});

const moreText = [
    'paragraphFormat',
    'bold',
    'italic',
    'formatUL',
    'formatOL',
    'underline',
    'subscript',
    'superscript',
    'outdent',
    'indent',
    'quote',
    'inlineClass',
];

const moreParagraph = ['alignLeft', 'alignCenter', 'alignRight'];

const moreRich = ['insertLink', 'insertVideo', 'insertImage', 'insertTable', 'insertHR', 'cardList'];

const moreMisc = ['spellChecker', 'undo', 'redo', 'clearFormatting', 'fullscreen', 'html', 'help'];

export default {
    name: 'RichTextEditor',

    components: {
        SafeFroalaWrapper,
    },

    props: {
        modelValue: {
            type: String,
            default: '',
        },

        value: {
            type: String,
            default: '',
        },

        placeholder: {
            type: String,
            default: 'Start typing...',
            required: false,
        },

        isDisabled: {
            type: Boolean,
            default: false,
            required: false,
        },

        enableSticky: {
            type: Boolean,
            default: true,
            required: false,
        },

        height: {
            type: Number,
            default: null,
            required: false,
        },

        heightMax: {
            type: Number,
            default: null,
            required: false,
        },

        importedCsv: {
            type: Boolean,
            default: false,
            required: false,
        },

        enableWrapper: {
            type: Boolean,
            default: false,
            required: false,
        },

        compactToolbar: {
            type: Boolean,
            default: false,
            required: false,
        },

        config: {
            type: Object,
            default: () => ({}),
        },

        isSaving: {
            type: Boolean,
            default: false,
        },

        allowImagePasting: {
            type: Boolean,
            default: true,
        },

        extendedRichEditor: {
            type: Boolean,
            default: true,
        },

        showCharCounter: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['update:modelValue', 'input', 'blur', 'ready', 'error'],

    data: () => ({
        model: '',
        baseConfig: {},
        editorInstance: null,
    }),

    computed: {
        mergedConfig() {
            return {
                ...this.baseConfig,
                ...this.config,
            };
        },
    },

    watch: {
        model(newModel) {
            // Only emit if the value actually changed and it's different from the current prop value
            const currentValue = this.modelValue || this.value;
            if (newModel !== currentValue) {
                this.$emit('update:modelValue', newModel);
                // Backward compatibility: emit input event
                this.$emit('input', newModel);
            }
        },

        modelValue: {
            handler(newValue) {
                // Only update internal model if the new value is different
                // This prevents the circular update issue during autosave
                // Also check if the editor instance exists and has focus to prevent interrupting typing
                if (newValue !== this.model) {
                    // If the editor is focused and the user is actively typing, don't update
                    if (
                        this.editorInstance &&
                        this.editorInstance.el &&
                        document.activeElement &&
                        this.editorInstance.el.contains(document.activeElement)
                    ) {
                        // User is actively typing, skip this update
                        return;
                    }
                    this.model = newValue || '';
                }
            },
            immediate: false,
        },

        value: {
            handler(newValue) {
                // Backward compatibility: handle v-model
                // Only update if modelValue is not being used (backward compatibility)
                if (!this.modelValue && newValue !== this.model) {
                    // If the editor is focused and the user is actively typing, don't update
                    if (
                        this.editorInstance &&
                        this.editorInstance.el &&
                        document.activeElement &&
                        this.editorInstance.el.contains(document.activeElement)
                    ) {
                        // User is actively typing, skip this update
                        return;
                    }
                    this.model = newValue || '';
                }
            },
            immediate: false,
        },

        importedCsv() {
            if (this.importedCsv) {
                this.model = this.modelValue || this.value;
                this.$emit('update:modelValue', this.model);
                // Backward compatibility: emit input event
                this.$emit('input', this.model);
            }
        },

        isDisabled(newValue) {
            if (this.editorInstance) {
                if (newValue) {
                    this.editorInstance.edit.off();
                } else {
                    this.editorInstance.edit.on();
                }
            }
        },
    },

    created() {
        // Initialize model with proper fallback
        this.model = this.modelValue || this.value || '';

        this.$nextTick(() => {
            this.initializeEditor();
        });

        const instance = this;
        let pendingImageFile = null;

        const moreSpecial = this.canUseEnhance() ? ['enhance'] : [];

        this.baseConfig = {
            videoUpload: false,
            iframe: false,
            imagePaste: this.allowImagePasting,
            key: initialState('Froala.key'),
            scaytAutoload: true,
            scaytCustomerId: initialState('Froala.spellchecker_key'),
            events: {
                'image.removed': $img => {
                    const path = $img[0].getAttribute('data-path');

                    if (path) {
                        // Extract the path that matches our expected format
                        const imagePath = path;

                        // Handle root/CDN paths - extract just the relevant part
                        if (path.includes('/uploaded-images/')) {
                            const matches = path.match(/\/uploaded-images\/(\d+)\/([^/]+)$/);
                            if (matches && matches.length === 3) {
                                // Directly use the extracted userId and fileName
                                const userId = matches[1];
                                const fileName = matches[2];
                                axios.delete(`/api/uploaded-images/${userId}/${fileName}`);
                                return;
                            }
                        }

                        // Fallback to old method if pattern matching fails
                        const pathParts = imagePath.split('/');
                        const userId = pathParts[pathParts.length - 2];
                        const fileName = pathParts[pathParts.length - 1];

                        // Construct the proper API endpoint for image deletion
                        axios.delete(`/api/uploaded-images/${userId}/${fileName}`);
                    }
                },

                'image.beforeUpload': images => {
                    pendingImageFile = images?.[0] ?? null;
                },

                'image.error': function (error) {
                    const reason = FROALA_IMAGE_ERROR_REASONS[error?.code];

                    if (reason && pendingImageFile) {
                        reportFileUploadRejection(
                            FILE_UPLOAD_CONTEXT.EDITOR_INLINE_IMAGE,
                            pendingImageFile,
                            reason,
                            reason === FILE_UPLOAD_REJECTION_REASON.FILE_TOO_LARGE
                                ? { maxFileSizeBytes: this.opts?.imageMaxSize }
                                : {}
                        );
                    }

                    pendingImageFile = null;
                },

                initialized() {
                    instance.disable(this);
                    instance.editorInstance = this;

                    // Disable grammarly chrome extension from loading in the editor.
                    this.$el[0].setAttribute('data-gramm', 'false');

                    // Binding these events in `initialized` allow us to prevent default Froala
                    // events from occurring if we need to.
                    this.events.on(
                        'keydown',
                        event => {
                            if (event.which === 13) {
                                return instance.handleInserts(this, event);
                            }

                            return true;
                        },
                        true
                    );

                    instance.$emit('ready', this);
                },

                blur() {
                    instance.$emit('blur');
                },
            },
            imageUploadParam: 'image',
            imageUploadParams: { _token: window.csrf_token },
            imageUploadURL: '/api/uploaded-images',
            imageUploadMethod: 'POST',
            charCounterCount: this.showCharCounter,
            wordCounter: false,
            attribution: true,
            tableMultipleStyles: true,
            tabSpaces: 4,
            toolbarSticky: this.enableSticky,
            lineHeights: {
                Default: '',
                Single: '1',
                1.15: '1.15',
                1.5: '1.5',
                Double: '2',
            },
            inlineClasses: {
                'is-white': 'White',
                'is-highlighted': 'Highlight',
                'is-important': 'Important',
                'is-success': 'Success',
                'is-info': 'Info',
                'is-warning': 'Warning',
                'is-error': 'Error',
            },
            tableStyles: {
                noBorder: 'No Border',
                striped: 'Striped Rows',
                dashed: 'Dashed Borders',
            },
            tableCellStyles: {
                noCellBorder: 'No Border',
                cellBorder: 'Solid Border',
                dashedCellBorder: 'Dashed Border',
                leftCellBorder: 'Left Border',
                rightCellBorder: 'Right Border',
                topCellBorder: 'Top Border',
                bottomCellBorder: 'Bottom Border',
            },
            allowStyle: true,
            toolbarButtonsXS: {
                moreText: {
                    buttons: moreText,
                    align: 'left',
                    buttonsVisible: 0,
                },
                moreParagraph: {
                    buttons: moreParagraph,
                    align: 'left',
                    buttonsVisible: 0,
                },
                moreRich: this.extendedRichEditor
                    ? {
                          buttons: moreRich,
                          align: 'left',
                          buttonsVisible: 0,
                      }
                    : {},
                moreSpecial: {
                    buttons: moreSpecial,
                    align: 'left',
                    buttonsVisible: 1,
                },
                moreMisc: {
                    buttons: moreMisc,
                    align: 'right',
                    buttonsVisible: 0,
                },
            },
            toolbarButtons: {
                moreSpecial: {
                    buttons: moreSpecial,
                    align: 'left',
                    buttonsVisible: 5,
                },
                moreText: {
                    buttons: moreText,
                    align: 'left',
                    buttonsVisible: this.compactToolbar ? 3 : 5,
                },
                moreParagraph: {
                    buttons: moreParagraph,
                    align: 'left',
                    buttonsVisible: this.compactToolbar ? 3 : 5,
                },
                moreRich: this.extendedRichEditor
                    ? {
                          buttons: moreRich,
                          align: 'left',
                          buttonsVisible: this.compactToolbar ? 3 : 5,
                      }
                    : {},
                moreMisc: {
                    buttons: moreMisc,
                    align: 'right',
                    buttonsVisible: this.compactToolbar ? 2 : 4,
                },
            },
            videoInsertButtons: ['videoByURL', '|', 'videoEmbed'],
        };

        if (this.height) {
            this.baseConfig.height = this.height;
        }

        if (this.heightMax != null) {
            const maxH = Number(this.heightMax);
            if (Number.isFinite(maxH) && maxH > 0) {
                this.baseConfig.heightMax = maxH;
            }
        }

        this.baseConfig.placeholderText = this.placeholder;
        this.baseConfig.pastePlain = false;
        this.baseConfig.wordPasteModal = false;
        this.baseConfig.wordPasteKeepFormatting = false;
        // `color` is intentionally omitted — SafeFroalaWrapper also strips inline
        // text colors on paste so Word/Docs pastes inherit theme text color.
        this.baseConfig.wordAllowedStyleProps = [
            'font-family',
            'font-size',
            'background',
            'width',
            'text-align',
            'vertical-align',
            'background-color',
            'padding',
            'margin',
            'height',
            'margin-top',
            'margin-left',
            'margin-right',
            'margin-bottom',
            'text-decoration',
            'font-weight',
            'font-style',
            'text-indent',
            'border',
            'border-.*',
        ];
        this.baseConfig.requestHeaders = {
            'X-XSRF-TOKEN': window.csrf_token,
        };
    },

    methods: {
        canUseEnhance() {
            const courseId =
                this.$route && this.$route.params && this.$route.params.courseId
                    ? Number.parseInt(this.$route.params.courseId, 10)
                    : null;

            const authUser =
                this.$store && this.$store.state && this.$store.state.Auth ? this.$store.state.Auth.user : null;

            const isStudentInCourse =
                courseId && authUser && typeof authUser.isStudentOf === 'function'
                    ? authUser.isStudentOf(courseId)
                    : false;

            return this.$hasFeature('ENHANCE') && !isStudentInCourse;
        },

        handleUpdate(value) {
            // Only update the model if the value is actually different
            // This prevents unnecessary reactive updates during autosave
            if (this.model !== value) {
                this.model = value;
            }
        },

        handleInitialized(editor) {
            this.editorInstance = editor;
            this.$emit('ready', editor);
        },

        handleError(error) {
            console.error('Editor error:', error);
            this.$emit('error', error);
        },

        handleInserts(editor, event) {
            const node = editor.selection.get().baseNode;

            // Find the first insert that should be injected
            const insertToInject = inserts.find(insert => editor.node.hasClass(node, insert.enterInsertClass));

            if (insertToInject) {
                // If an insert is found, inject its HTML and remove the original node
                editor.html.insert(insertToInject.enterInsertHtml);
                node.remove();

                // Prevent Froala from adding a newline after our insert
                event.preventDefault();
                event.stopImmediatePropagation();

                return false;
            }

            return true;
        },

        disable(editor) {
            if (this.isDisabled) {
                editor.edit.off();
            }
        },

        initializeEditor() {
            // Only set the model if it's not already initialized to prevent overwriting user input
            if (!this.model) {
                this.model = this.modelValue || this.value || '';
            }
        },
    },
};
</script>

<style lang="scss">
.editor-wrapper {
    position: relative;

    &.is-saving::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.7);
        z-index: 1;
    }
}

.editor-wrapper .fr-toolbar.fr-top {
    top: auto !important;
    position: relative !important;
}

/* Match Froala's own hover/focus pattern so the whole editor turns red without coloring the wrapper/footer divider */
.required-rich-text-error .fr-box.fr-basic .fr-wrapper {
    border-left-color: #ff3860 !important;
    border-right-color: #ff3860 !important;
}

.required-rich-text-error .fr-box.fr-basic .fr-toolbar,
.required-rich-text-error .fr-box.fr-basic .fr-second-toolbar {
    border-color: #ff3860 !important;
}
</style>
